import {
  TextAreaField,
  TextInputField,
  useModalState,
  Button,
  Modal,
  Flex
} from '@sequencehq/core-components'
import { cloneElement, ReactElement } from 'react'
import { useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import { TaxCategory } from '../taxRates.types'

type FormValues = {
  name: string
  description: string
}

interface Props {
  trigger: ReactElement
  onSubmit: (values: FormValues, taxCategory?: TaxCategory) => Promise<void>
  taxCategory?: TaxCategory
}

const TaxCategoryModalUI = ({
  taxCategory,
  onSubmit,
  onClose
}: Omit<Props, 'trigger'> & { onClose: () => void }) => {
  const isEditing = Boolean(taxCategory?.id)

  const { fields: fieldsConfig, queries } = useForm<FormValues>({
    value: {
      name: taxCategory?.name ?? '',
      description: taxCategory?.description ?? ''
    },
    fieldConfiguration: [
      {
        property: 'name',
        validation: [required]
      },
      {
        property: 'description',
        validation: []
      }
    ]
  })

  const handleSubmit = async (formValues: FormValues) => {
    await onSubmit(formValues, taxCategory)
    onClose()
  }

  return (
    <>
      <Modal.Header showCloseButton withBorder onClose={onClose}>
        {isEditing ? 'Edit tax category' : 'Add tax category'}
      </Modal.Header>

      <Modal.Content minHeight="auto">
        <Flex flexDirection="column" gap="16px">
          <TextInputField
            label="Name"
            autoFocus
            value={fieldsConfig.name.value}
            validationErrors={fieldsConfig.name.validationErrors}
            onChange={event => fieldsConfig.name.onChange(event.target.value)}
            placeholder="Tax category name"
            data-testid="taxCategoryModal.name"
          />
          <TextAreaField
            label="Description"
            value={fieldsConfig.description.value}
            validationErrors={fieldsConfig.description.validationErrors}
            onChange={fieldsConfig.description.onChange}
            placeholder="Tax category description"
            data-testid="taxCategoryModal.description"
          />
        </Flex>
      </Modal.Content>

      <Modal.Footer withBorder>
        <Button
          onClick={onClose}
          style={{ marginLeft: 'auto' }}
          variant="secondary"
        >
          Cancel
        </Button>

        <Button
          variant="primary"
          type="button"
          disabled={!queries.isValid}
          onClick={() => {
            handleSubmit(queries.formData)
          }}
        >
          {isEditing ? 'Edit tax category' : 'Create tax category'}
        </Button>
      </Modal.Footer>
    </>
  )
}

export const TaxCategoryModal = ({ taxCategory, trigger, onSubmit }: Props) => {
  const { isModalOpen, openModal, closeModal } = useModalState()
  const triggerWithOnClick = cloneElement(trigger, {
    onClick: (event: React.MouseEvent) => {
      if (trigger.props.onClick) {
        trigger.props.onClick(event)
      }

      openModal()
    }
  })
  return (
    <>
      {triggerWithOnClick}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        data-testid="taxCategoryModal"
      >
        <TaxCategoryModalUI
          onSubmit={onSubmit}
          taxCategory={taxCategory}
          onClose={closeModal}
        />
      </Modal>
    </>
  )
}
